// @ts-nocheck
/**
 * @file Robot serial, alias and endusername
 * @author Max van Loosbroek
 */

import React, { useState, useEffect, ChangeEvent } from 'react'
import { ProfileRobotUser } from '../generalSettings/UserProfile/redux/types'
import {
  Item,
  LeftColumn,
  MiddleColumn,
  RightColumn
} from '../../common/components/ItemList/ItemList'
import { TooltipMaterial } from '../../common/components/TooltipMaterial/TooltipMaterial'
import { SvgIcon, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ReactComponent as RobotIcon } from '../../assets/images/logo/tessa_creme.svg'
import { isEmpty } from 'lodash'
import { usePrevious } from '../../common/hooks/usePreviousValue'
import fastDeepEqual from 'fast-deep-equal'
import { StyledTextField } from '../../common/components/StyledTextField/StyledTextField'
import { useTheme } from '@mui/material'
import styledComponentsTheme from '../../common/constants/styledComponentsTheme/styledComponentsTheme'
export interface RobotProfileProps {
  robot: ProfileRobotUser
  setProfilePatch: React.Dispatch<React.SetStateAction<{ endUserName?: string | undefined }>>
  setRobotUserPatch: React.Dispatch<React.SetStateAction<{ alias?: string | undefined }>>
  onSubmit: (...args: any) => any
  showProfile: boolean
  setShowProfile: (show: boolean) => any
  authUserIsPrimary: boolean
}

const minLength = 2
const maxLengthAlias = 26
const maxLengthEndUserName = 64

export const endUserNameLengthValid = (endUserName?: string): boolean => {
  if (endUserName === undefined || endUserName === null) return false
  return endUserName.length >= minLength && endUserName.length <= maxLengthEndUserName
}

export const aliasLengthValid = (alias?: string): boolean => {
  if (alias === undefined || alias === null) return false
  return alias.length >= minLength && alias.length <= maxLengthAlias
}

export const RobotProfile: React.FunctionComponent<RobotProfileProps> = ({
  robot,
  setProfilePatch,
  setRobotUserPatch,
  onSubmit,
  showProfile,
  setShowProfile,
  authUserIsPrimary
}: RobotProfileProps) => {
  const theme = useTheme()

  const [endUserName, setEndUserName] = useState<string | undefined>(
    robot.endUserName !== null ? robot.endUserName : undefined
  )
  const [alias, setAlias] = useState<string>(
    robot.alias === null ? robot.robot : robot.alias
  )
  const previousRobot = usePrevious(robot)
  const aliasError = !isEmpty(alias) && !aliasLengthValid(alias)
  const endUserNameError = endUserName !== null && endUserName !== undefined && !endUserNameLengthValid(endUserName)
  const { t } = useTranslation()
  const helperTextEndUserName = t('ROBOT_SETTINGS.PROFILE.ERROR_END_USER')
  const helperTextAlias = t('ROBOT_SETTINGS.PROFILE.ERROR_ALIAS')

  useEffect(() => {
    setProfilePatch({ endUserName })
    setRobotUserPatch({ alias })
  }, [endUserName, alias])

  useEffect(() => {
    if (!fastDeepEqual(robot, previousRobot)) {
      setEndUserName(robot.endUserName ?? undefined)
      setAlias(robot.alias ?? '')
    }
  }, [previousRobot, robot])

  return (
    <Box sx={{ marginBottom: 1.5 }}>
      <Item height={32} onClick={() => setShowProfile(!showProfile)}>
        <LeftColumn>
          <TooltipMaterial
            placement='bottom-start'
            title={t('ROBOT_SETTINGS.TOOLTIPS.SERIAL')}
          >
            <SvgIcon
              sx={{ fontSize: '22px' }}
              component={RobotIcon}
              viewBox='0 0 20 23'
            />
          </TooltipMaterial>
        </LeftColumn>
        {/* @ts-ignore styled MiddleColumn accepts standard div props */}
        <MiddleColumn
          sx={{
            fontFamily: 'Roboto-Mono, monospace',
            textDecoration: 'underline'
          }}
        >
          <span style={{ fontWeight: 200 }}>
            {robot.robot
              .match(/.{1,4}/g)
              ?.splice(0, 3)
              .join(' ')}
          </span>
          &nbsp;
          <span>
            {robot.robot
              .match(/.{1,4}/g)
              ?.splice(3, 3)
              .join(' ')}
          </span>
        </MiddleColumn>
      </Item>
      <Box sx={{ display: showProfile ? 'block' : 'none' }}>
        <Item disabled={!authUserIsPrimary} height='auto' sx={{ marginTop: 1.25 }}>
          <LeftColumn />
          <MiddleColumn sx={{ flexGrow: 1, display: 'flex' }}>
            <Box component='form' sx={{ flexGrow: 1, lineHeight: '20px', display: 'flex', flexWrap: 'wrap' }} onSubmit={onSubmit}>
              <Box sx={{ paddingTop: 0.625 }}>
                {t('ROBOT_SETTINGS.PROFILE.FORM.END_USER.LABEL')}:&nbsp;
              </Box>
              <Box sx={{ flexGrow: 1, marginLeft: 0 }}>
                <StyledTextField
                  id='endUserName'
                  ariaLabel='End user name input'
                  type='text'
                  value={!authUserIsPrimary ? endUserName ?? '-' : endUserName}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => setEndUserName(e.target.value)}
                  placeholder={t('ROBOT_SETTINGS.PROFILE.FORM.END_USER.PLACEHOLDER')}
                  disabled={!authUserIsPrimary}
                  disableUnderline={!authUserIsPrimary}
                  error={endUserNameError}
                  helperText={endUserNameError ? helperTextEndUserName : ''}
                  sx={{
                    width: '100%',
                    flexGrow: 1,
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      fontFamily: 'Montserrat',
                      color: styledComponentsTheme.colors.cremewhite,
                      width: '100%',
                      transition: theme.transitions.create(['background-color', 'color'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      paddingLeft: 0,
                      '&:focus': {
                        backgroundColor: 'transparent'
                      }
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiInputBase-root.Mui-disabled': {
                      color: styledComponentsTheme?.colors.lightgrey,
                      pointerEvents: 'none',
                      backgroundColor: 'transparent',
                      paddingLeft: 0
                    }
                  }}
                />
              </Box>
            </Box>
          </MiddleColumn>
          <RightColumn />
        </Item>
        <Item height='auto' sx={{ marginTop: 1.25, marginBottom: 2.5 }}>
          <LeftColumn />
          <MiddleColumn sx={{ flexGrow: 1, display: 'flex' }}>
            <Box component='form' sx={{ flexGrow: 1, lineHeight: '20px', display: 'flex', flexWrap: 'wrap' }} onSubmit={onSubmit}>
              <Box sx={{ paddingTop: 0.625 }}>
                {t('ROBOT_SETTINGS.PROFILE.FORM.ALIAS.LABEL')}:&nbsp;
              </Box>
              <Box sx={{ flexGrow: 1, marginLeft: 0 }}>
                <StyledTextField
                  id='alias'
                  ariaLabel='Alias input'
                  type='text'
                  value={alias}
                  onChange={e => setAlias(e.target.value)}
                  error={aliasError}
                  helperText={aliasError ? helperTextAlias : ''}
                  sx={{
                    width: '100%',
                    flexGrow: 1,
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      fontFamily: 'Montserrat',
                      color: styledComponentsTheme.colors.cremewhite,
                      width: '100%',
                      transition: theme.transitions.create(['background-color', 'color'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      paddingLeft: 0,
                      '&:focus': {
                        backgroundColor: 'transparent'
                      }
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiInputBase-root.Mui-disabled': {
                      color: styledComponentsTheme?.colors.lightgrey,
                      pointerEvents: 'none',
                      backgroundColor: 'transparent',
                      paddingLeft: 0
                    }
                  }}
                />
              </Box>
            </Box>
          </MiddleColumn>
          <RightColumn />
        </Item>
      </Box>
    </Box>
  )
}
